import { useEffect, useRef, useState } from 'react';
import type { LngLat } from './types.ts';
import { distanceM } from './geo.ts';
import { startWatch, isNative, gpsCounts, noteRearm, type LocationWatch, type RawFix } from './locationSource.ts';
import { pushDiag } from './yardageWidget.ts';

export interface Fix {
  pos: LngLat;
  accuracy: number;
  speed: number; // m/s
  heading: number; // degrees, 0 = north
  ts: number;
  stale: boolean;
}

interface Options {
  /** Fixes worse than this are dropped outright. Head units love to emit 500m fixes. */
  maxAccuracy?: number;
  /** After this long with no fix, the reading is marked stale. */
  staleAfterMs?: number;
  simulate?: LngLat | null;
}

/**
 * Smoothed GPS, from whichever source this platform offers.
 *
 * A buggy moves slowly and GPS jitters ~3-5m at rest, which makes the yardage
 * number flicker and looks broken. This applies:
 *   1. an accuracy gate,
 *   2. a jump gate (reject fixes implying > 25 m/s),
 *   3. an adaptive low-pass — heavy smoothing when stopped, light when moving,
 *      so the number is rock-steady standing over a shot but still tracks the
 *      buggy down the fairway.
 *
 * On a phone the underlying watcher is a native background service, so this
 * keeps producing fixes with the screen off and the phone in a pocket. In a
 * browser it is navigator.geolocation, which stops when the screen locks.
 */
export function useGps(opts: Options = {}) {
  const { maxAccuracy = 40, staleAfterMs = 12_000, simulate = null } = opts;
  const [fix, setFix] = useState<Fix | null>(null);
  const [error, setError] = useState<string | null>(null);
  const last = useRef<Fix | null>(null);

  useEffect(() => {
    if (simulate) {
      const f: Fix = {
        pos: simulate,
        accuracy: 3,
        speed: 0,
        heading: 0,
        ts: Date.now(),
        stale: false,
      };
      last.current = f;
      setFix(f);
      return;
    }

    let watch: LocationWatch | null = null;
    let cancelled = false;

    const onFix = (raw: RawFix) => {
      if (raw.accuracy > maxAccuracy) return;

      const prev = last.current;
      let pos = raw.pos;
      let heading = raw.heading ?? prev?.heading ?? 0;

      if (prev) {
        const dt = Math.max((raw.ts - prev.ts) / 1000, 0.001);
        const jump = distanceM(prev.pos, raw.pos);
        if (jump / dt > 25) return; // physically impossible for a buggy

        const speed = raw.speed || jump / dt;
        // alpha: 0.15 parked (very smooth) -> 0.75 rolling (responsive)
        const alpha = Math.min(0.75, 0.15 + Math.max(speed, 0) * 0.12);
        pos = [
          prev.pos[0] + (raw.pos[0] - prev.pos[0]) * alpha,
          prev.pos[1] + (raw.pos[1] - prev.pos[1]) * alpha,
        ];
        if (raw.heading == null && jump > 2) {
          heading =
            ((Math.atan2(raw.pos[0] - prev.pos[0], raw.pos[1] - prev.pos[1]) * 180) / Math.PI +
              360) %
            360;
        }
      }

      const next: Fix = {
        pos,
        accuracy: raw.accuracy,
        speed: raw.speed,
        heading,
        ts: raw.ts,
        stale: false,
      };
      last.current = next;
      setFix(next);
      setError(null);
    };

    let lastDelivery = Date.now();
    const wrappedFix = (raw: RawFix) => {
      lastDelivery = Date.now();
      onFix(raw);
    };

    const begin = () => {
      startWatch(wrappedFix, (msg) => setError(msg))
        .then((w) => {
          if (cancelled) void w.stop();
          else watch = w;
        })
        .catch((e) => setError((e as Error).message));
    };
    begin();

    /**
     * Watchdog.
     *
     * The background plugin has a documented iOS bug: after roughly an hour
     * backgrounded it simply stops emitting, and only wakes when the screen is
     * turned on. A round is four hours, so left alone that is a tile that dies
     * at the sixth hole and a golfer who has no idea why.
     *
     * Rather than trust it, this notices that nothing has arrived for a while
     * and rebuilds the watcher. Restarting a healthy watcher would be wasteful,
     * so the check is generous — a phone genuinely still gets fixes every few
     * seconds, and two minutes of total silence is a fault, not a quiet patch.
     */
    const REARM_AFTER_MS = 120_000;
    const watchdog = window.setInterval(() => {
      if (cancelled || Date.now() - lastDelivery < REARM_AFTER_MS) return;
      lastDelivery = Date.now();
      noteRearm();
      const dying = watch;
      watch = null;
      void dying?.stop().catch(() => {});
      begin();
    }, 30_000);

    /*
     * GPS health, heartbeated into the widget's container.
     *
     * Every question asked of this code so far — is the background watcher
     * alive, is anything arriving, is the watchdog thrashing — was unanswerable
     * without holding the phone. Writing it out on a timer makes a locked phone
     * on a desk fully inspectable, which is the only way the screen-off case
     * can be tested honestly.
     */
    const diag = window.setInterval(() => {
      const now = Date.now();
      const l = last.current;
      void pushDiag({
        bg: gpsCounts.bg,
        cl: gpsCounts.cl,
        rearms: gpsCounts.rearms,
        bgAgeS: gpsCounts.lastBg ? Math.round((now - gpsCounts.lastBg) / 1000) : -1,
        clAgeS: gpsCounts.lastCl ? Math.round((now - gpsCounts.lastCl) / 1000) : -1,
        fixAgeS: l ? Math.round((now - l.ts) / 1000) : -1,
        accuracy: l ? Math.round(l.accuracy) : -1,
        stale: l?.stale ?? true,
      });
    }, 20_000);

    const staleTimer = window.setInterval(() => {
      const l = last.current;
      if (l && Date.now() - l.ts > staleAfterMs && !l.stale) {
        const s = { ...l, stale: true };
        last.current = s;
        setFix(s);
      }
    }, 3000);

    return () => {
      cancelled = true;
      void watch?.stop();
      window.clearInterval(staleTimer);
      window.clearInterval(watchdog);
      window.clearInterval(diag);
    };
  }, [maxAccuracy, staleAfterMs, simulate]);

  return { fix, error, backgroundCapable: isNative() };
}
